import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { BookText, ChevronDown, LogOut, ShieldCheck, User } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const planLabel = (plan) => {
  const map = { free: "Free", solo: "Solo", pro: "Pro", agency: "Agency", starter: "Solo" };
  return map[plan] || "Free";
};

const TopNav = ({ minimal = false }) => {
  const { session, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const initials = (profile?.full_name || profile?.email || "U").slice(0, 1).toUpperCase();
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!session || minimal) return;
    let mounted = true;
    api.get("/books").then((r) => {
      if (mounted) setCounts(r.data?.counts || null);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [session, minimal]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <header
      data-testid="top-nav"
      className="w-full border-b border-[#D3D1C7]/70 bg-[#FAFAF9]/80 backdrop-blur-md sticky top-0 z-40"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[68px] flex items-center justify-between">
        <Link to={session ? "/dashboard" : "/"} className="flex items-center gap-2.5" data-testid="logo-link">
          <div className="w-8 h-8 rounded-md bg-[#534AB7] flex items-center justify-center shadow-sm">
            <BookText className="w-4 h-4 text-white" strokeWidth={1.8} />
          </div>
          <span className="font-medium tracking-tight text-[17px] text-[#2C2C2A]" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
            PublishMate
          </span>
        </Link>
        {!minimal && (
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#5F5E5A]">
            {!session && (
              <>
                <Link to="/" className="hover:text-[#2C2C2A] transition-colors" data-testid="nav-home">Home</Link>
                <Link to="/pricing" className="hover:text-[#2C2C2A] transition-colors" data-testid="nav-pricing">Pricing</Link>
                <Link
                  to="/login"
                  className="px-4 py-2 rounded-[8px] border border-[#D3D1C7] hover:bg-[#F1EFE8] transition-colors"
                  data-testid="nav-login"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 rounded-[8px] bg-[#534AB7] text-white hover:bg-[#3C3489] transition-colors shadow-sm"
                  data-testid="nav-signup"
                >
                  Start free
                </Link>
              </>
            )}
            {session && (
              <>
                <Link to="/dashboard" className="hover:text-[#2C2C2A] transition-colors" data-testid="nav-dashboard">Dashboard</Link>
                <Link
                  to="/books"
                  className="hover:text-[#2C2C2A] transition-colors inline-flex items-center gap-1.5"
                  data-testid="nav-books"
                >
                  Books
                  {counts && counts.total > 0 && (
                    <span
                      className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#F1EFE8] text-[#5F5E5A] tabular-nums border border-[#D3D1C7]"
                      data-testid="nav-books-counter"
                      title={`${counts.ready} ready · ${counts.draft} draft`}
                    >
                      {counts.ready}/{counts.total}
                    </span>
                  )}
                </Link>
                <Link to="/pricing" className="hover:text-[#2C2C2A] transition-colors" data-testid="nav-pricing-2">Pricing</Link>
              </>
            )}
          </nav>
        )}
        {session && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 px-2 py-1.5 rounded-[8px] hover:bg-[#F1EFE8] transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-[#534AB7] text-white text-sm font-medium flex items-center justify-center">
                  {initials}
                </div>
                <span
                  data-testid="plan-badge"
                  className="hidden sm:inline-flex text-xs px-2 py-0.5 rounded-full bg-[#F1EFE8] text-[#534AB7] uppercase tracking-wider font-medium border border-[#D3D1C7]"
                >
                  {planLabel(profile?.plan)}
                </span>
                <ChevronDown className="w-4 h-4 text-[#5F5E5A]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-xs text-[#5F5E5A]">{profile?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                <User className="w-4 h-4 mr-2" /> Dashboard
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account">
                <User className="w-4 h-4 mr-2" /> Account & billing
              </DropdownMenuItem>
              {profile?.is_admin && (
                <DropdownMenuItem onClick={() => navigate("/admin/codes")} data-testid="menu-admin-codes">
                  <ShieldCheck className="w-4 h-4 mr-2" /> Admin · Promo codes
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut} data-testid="menu-signout">
                <LogOut className="w-4 h-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
};

export default TopNav;
